import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { inputClass, iconButtonClass, cn } from '../../utils/theme'
import { useDebouncedValue } from '../../utils/debounce'

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  onDebouncedChange?: (value: string) => void
  placeholder?: string
  debounceDelay?: number
}

export function SearchBar({
  value,
  onChange,
  onDebouncedChange,
  placeholder = 'Buscar productos…',
  debounceDelay = 300,
}: SearchBarProps) {
  const [internalValue, setInternalValue] = useState(value)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const debouncedValue = useDebouncedValue(internalValue, debounceDelay)

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  useEffect(() => {
    onDebouncedChange?.(debouncedValue)
  }, [debouncedValue, onDebouncedChange])

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const isModifier = event.metaKey || event.ctrlKey || event.altKey
      const activeTag = (event.target as HTMLElement | null)?.tagName

      if (!isModifier && event.key === '/' && activeTag !== 'INPUT' && activeTag !== 'TEXTAREA') {
        event.preventDefault()
        inputRef.current?.focus()
      }
    }

    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [])

  const clearSearch = () => {
    setInternalValue('')
    onChange('')
    onDebouncedChange?.('')
    inputRef.current?.focus()
  }

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor="products-search"
        className="text-sm font-medium text-neutral-700 dark:text-neutral-200"
      >
        Buscar
        <span className="ml-2 rounded bg-neutral-200 px-1.5 py-0.5 text-xs text-neutral-700 dark:bg-neutral-700 dark:text-neutral-100">
          /
        </span>
      </label>
      <div className="relative">
        <input
          id="products-search"
          ref={inputRef}
          type="search"
          value={internalValue}
          onChange={(event) => {
            setInternalValue(event.target.value)
            onChange(event.target.value)
          }}
          placeholder={placeholder}
          className={cn(inputClass(), 'pr-10')}
          aria-label="Buscar productos por nombre"
        />
        {internalValue ? (
          <button
            type="button"
            onClick={clearSearch}
            className={cn(iconButtonClass(), 'absolute inset-y-0 right-2 my-1 h-7 w-7')}
            aria-label="Limpiar búsqueda"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default SearchBar
